/* eslint-disable react-doctor/no-inline-exhaustive-style */
"use client";

import Link from "next/link";
import type { Sound } from "@/lib/supabase";
import { TPlay } from "@/components/ui/tls/t-play";
import { cleanSoundTitle } from "@/lib/utils";
import { splitTitle } from "@/lib/plate-and-hairline";
import { TESLA_REQUIREMENTS } from "@/lib/audio-utils";
import { soundUrl } from "@/lib/slugify";
import { RowMeta, rowMetaTokens } from "./row-meta";

export interface StaticSoundRowProps {
  sound: Sound;
  showCategory?: boolean;
  /** §04.1 density axis, mirrored from SoundCard so the geometry matches. */
  density?: "d2";
  /** Related-list form; see SoundCardProps.related. */
  related?: boolean;
}

/**
 * The title's parenthetical, shown as the row's second line instead of living
 * only in an aria-label. Renders nothing for a title without one.
 */
export function RowDescriptor({ title }: { title: string }) {
  const { descriptor } = splitTitle(title);
  if (!descriptor) return null;
  return <p className="sc-descriptor">{descriptor}</p>;
}

/**
 * The resting SoundCard row, rendered without its hook stack.
 *
 * Windowed hydration (sort-grid.tsx, sound-grid.tsx) keeps every slot past
 * the eager window from mounting a full SoundCard until it scrolls near the
 * viewport, because 200 cards' worth of audio / favourites / tracking hooks on
 * one post-load task was the dominant mobile TBT cost (TES-834, TES-1077).
 * The first version of that window rendered an EMPTY hairline band for the
 * deferred slots, which cost the server HTML every row past the sixth: a
 * collection page with 164 sounds shipped 6 links, and in dark theme the
 * first paint read as "six sounds, then a stack of empty rules".
 *
 * This is the same row — same `.sc-*` classes, same grid areas, same 68px /
 * 76px geometry — carrying the content that does not need a hook: the title
 * as a real link, the category and facts line, and the resting
 * control outlines. A crawler and a first paint see every row; the upgrade
 * to a live SoundCard on intersection swaps like for like, so it causes no
 * layout shift.
 *
 * Server render and first client render agree by construction, which is why
 * this exists rather than a `typeof IntersectionObserver` initialiser: that
 * made the server paint a full card and the client paint a placeholder, a
 * hydration mismatch on every deferred row.
 *
 * The transport and action outlines are decorative (`aria-hidden`): the row
 * upgrades 400px before it reaches the viewport, so nobody is asked to press
 * a control that is not yet wired.
 */
export function StaticSoundRow({
  sound,
  showCategory = true,
  density,
  related = false,
}: StaticSoundRowProps) {
  const titleId = `sound-row-title-${sound.id}`;
  const needsTrimming = (sound.duration || 0) > TESLA_REQUIREMENTS.MAX_DURATION;
  const tokens = rowMetaTokens({
    downloads: sound.downloads ?? 0,
    duration: sound.duration,
    needsTrimming,
    related,
  });

  return (
    <article
      aria-labelledby={titleId}
      data-testid="sound-card"
      data-hydration="deferred"
      data-row-density={density}
      className="sc-marquee"
    >
      <div className="sc-band" aria-hidden="true">
        <TPlay decorative playing={false} size="css" className="sc-play" />
      </div>

      <div className="sc-content">
        <Link prefetch={false}
          href={soundUrl(sound)}
          className="sc-title-link relative before:absolute before:inset-x-0 before:content-[''] sm:before:-inset-y-2"
          style={{ textDecoration: "none", display: "block", minWidth: 0 }}
        >
          <h3
            id={titleId}
            className="sc-title"
            style={{
              margin: 0,
              fontFamily: "var(--tls-font-display)",
              color: "var(--tls-fg-strong)",
            }}
          >
            {cleanSoundTitle(sound.title)}
          </h3>
        </Link>

        {related && <RowDescriptor title={sound.title} />}

        <RowMeta tokens={tokens} category={showCategory ? sound.category : null} />

        {/* Resting outlines of the download and save controls, so the title
            column is the same width before and after the upgrade. */}
        <div className="sc-card-actions" aria-hidden="true">
          <span className="sc-download" style={STATIC_CONTROL_STYLE} />
          {!related && <span className="sc-save" data-keep="" style={STATIC_CONTROL_STYLE} />}
        </div>
      </div>
    </article>
  );
}

const STATIC_CONTROL_STYLE: React.CSSProperties = {
  display: "inline-flex",
  width: 44,
  height: 44,
  minWidth: 44,
  minHeight: 44,
  flexShrink: 0,
  borderRadius: "var(--ls-radius-round)",
  border: "1px solid var(--tls-border-subtle)",
};
